// Protected route for Designer — superuser, connection owner, or a member
// whose role grants designer_access on the selected connection (Task 9,
// ERP Users & Access Rights epic; per-connection, not platform-wide).
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import { useConnection } from '@/app/contexts/ConnectionContext';
import { useMyPermissions } from '@/app/permissions/useMyPermissions';
import { hasDesignerAccess } from '@/app/permissions/permissionHelpers';

interface ProtectedDesignerRouteProps {
  children: React.ReactNode;
}

export const ProtectedDesignerRoute: React.FC<ProtectedDesignerRouteProps> = ({ children }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { selectedConnection, hydrated } = useConnection();
  const { perms, loading } = useMyPermissions();

  if (currentUser?.is_superuser) {
    return <>{children}</>;
  }
  if (!hydrated) {
    // Still figuring out whether a connection was ever saved at all — not
    // "resolved and denied", so don't redirect on this render.
    return null;
  }
  if (!selectedConnection) {
    // Hydrated, and genuinely no connection selected (fresh login, cleared,
    // or the saved one was deleted) — every Designer page needs one.
    return <Navigate to="/connections" replace />;
  }
  if (loading) {
    // A connection exists; its permissions are still resolving.
    return null;
  }
  if (perms?.is_owner || hasDesignerAccess(perms)) {
    return <>{children}</>;
  }

  return <Navigate to="/connections" replace />;
};

